import { ArrayOfAll } from '../../utils/ArrayOfAll';
import type {
    AgencyAlertKind,
    BaselineStanding,
    IInsightsFilter,
    DomainState,
    OverallState
} from '@innerhive-internal/innerhive-common';
import { clientDisplayName } from '@innerhive-internal/innerhive-common';

/**
 * One status palette for the whole tab, so a colour means the same thing wherever it appears: in the
 * caseload honeycomb, in a facet of someone's hexagon, and against a note in the detail view.
 *
 * Red is something that already happened. Coral is a decline against how this person usually is.
 * Blue is steady, and doubles as the ordinary observation on the weekly chart. Green is settled,
 * meaning less raised than usual rather than merely unremarkable. Grey is the one that encodes no
 * claim at all: not enough to compare, which is why it is the only recessive colour here.
 *
 * Checked as a set rather than chosen by eye. The green is NOT the brand's success token (#059669):
 * that sits 14.0 from the brand blue to normal vision, under the 15 floor, and steady-blue and
 * settled-green now share a legend where they have to be told apart at a glance. #2E7D32 is the
 * nearest credible green that clears it, at 16.3. The coral is 2.97:1 against a light surface, just
 * under 3:1, which is why nothing here is ever encoded by colour alone.
 */
export const STATUS = {
    incident: '#B3261E',
    declining: '#EE6C4E',
    steady: '#11789D',
    settled: '#2E7D32',
    // Measured the same way as the hues above: 17.4 from steady, its nearest neighbour, against the
    // 15 floor, and 3.55:1 on a light surface, which clears the 3:1 that the coral does not. Low
    // chroma on purpose, so a client nobody knows enough about recedes instead of competing with
    // the four readings that do mean something.
    unknown: '#7E8A8F'
};

export const KIND_ORDER: ArrayOfAll<AgencyAlertKind> = ['incident', 'baseline', 'observation'];

export const KIND_COLOR: { [key in AgencyAlertKind]: string } = {
    incident: STATUS.incident,
    baseline: STATUS.declining,
    // The ordinary case takes the brand blue rather than a recessive grey. On most weeks it is the
    // bulk of the bar, and a chart whose common case is drawn as absence reads as broken, not calm.
    observation: STATUS.steady
};

/**
 * The resting fill of a bar: the same hue at three quarters strength over white, so a column reads as
 * a solid lighter version of its kind rather than a see-through one.
 *
 * Three quarters and not less, which is the whole reason these are named colours rather than an
 * opacity. Tinting compresses the two warm hues toward each other: at half strength the incident red
 * and the declining coral fall to 9.2 apart, under the 15 floor, which is two touching bands of a
 * stacked column that nobody can tell apart. At three quarters they hold 15.1 to normal vision and
 * 10.7 under protanopia, and the legend carries a label for each, which is the relief the contrast
 * check asks for.
 *
 * The figures are CIEDE2000 against a white ground, with protanopia simulated by the Brettel-Vienot
 * transform. Another ΔE formula or CVD model will not reproduce them to the decimal, so re-measure
 * both hues the same way rather than comparing a new reading against these.
 */
export const KIND_TINT: { [key in AgencyAlertKind]: string } = {
    incident: '#C65C56',
    baseline: '#F2917A',
    observation: '#4D9AB6'
};

export const KIND_LABEL: { [key in AgencyAlertKind]: string } = {
    incident: 'Incident or ER',
    baseline: 'Worth Watching',
    observation: 'Observations'
};

/**
 * The same labels for use mid-sentence. They exist because lowercasing the display label is not the
 * same operation: "Incident or ER" becomes "incident or er", and ER is an abbreviation that keeps its
 * capitals wherever it appears. Anything writing a kind into running text uses these.
 */
export const KIND_SENTENCE_LABEL: { [key in AgencyAlertKind]: string } = {
    incident: 'incident or ER',
    baseline: 'worth watching',
    observation: 'observation'
};

/** What the kind means, for the legend's second line. Plain language, no jargon. */
export const KIND_DESCRIPTION: { [key in AgencyAlertKind]: string } = {
    incident: 'Something already happened: a fall, an ER trip, a hospital stay.',
    baseline: 'A change against how this client usually is.',
    observation: 'Everything else the record picked up, including anything missing on file.'
};

/**
 * Named states rather than a diverging scale with a neutral middle. Steady is its own blue, not
 * grey: on a caseload map most people are steady most of the time, and a map that renders the
 * common case as absence of colour looks broken rather than calm. Grey is kept for the client the
 * tab has nothing to say about, which is the one case that genuinely is an absence.
 */
export const STANDING_COLOR: { [key in BaselineStanding]: string } = {
    above: STATUS.declining,
    steady: STATUS.steady,
    settled: STATUS.settled,
    unknown: STATUS.unknown
};

/**
 * Worst first, so the legend and any list built from it agree on which end the reader starts at.
 * Typed for completeness, not just element type: adding a standing that nobody adds here would
 * otherwise drop silently out of the legend while the colour and label maps still compiled.
 *
 * Unknown sits at the end rather than anywhere among the three because it is not a point on the
 * scale. Ordering it between settled and steady would imply it is a mild reading of the same
 * measurement, when it is the statement that no measurement was possible.
 */
export const STANDING_ORDER: ArrayOfAll<BaselineStanding> = ['above', 'steady', 'settled', 'unknown'];

/** STANDING_LABEL in running text: written out rather than lowercased, so no label can be mangled by a case change. */
export const STANDING_SENTENCE_LABEL: { [key in BaselineStanding]: string } = {
    above: 'below baseline',
    steady: 'steady',
    settled: 'settled',
    unknown: 'not enough records yet'
};

/**
 * Why nothing can be said about a client, which the shared label cannot say for both reasons at once.
 * "Nothing recent on record" rather than "nothing recorded": what the tab cannot see, it cannot claim
 * did not happen.
 */
export const describeUnknownStanding = (params: { clientName: string; recordedRecently: boolean }): string => {
    const { clientName, recordedRecently } = params;
    const name = clientDisplayName(clientName);
    return recordedRecently
        ? `${name}: not enough history yet to compare against their own baseline`
        : `${name}: nothing recent on record to compare against their own baseline`;
};

/**
 * The agency header's own palette, brought down into the tab so the two read as one surface rather
 * than a branded band sitting on top of a generic dashboard. The deep teal carries headings and
 * structure; the coral is used sparingly, as an accent on the things that want attention.
 *
 * Distinct from the data hues above on purpose: these dress the chrome and never encode a value, so
 * they are free to be as dark or as saturated as the brand is.
 */
export const BRAND = {
    deepTeal: '#12414f',
    deeperTeal: '#0d3542',
    coral: '#EE6C4E',
    /**
     * The same hue at 5.15:1 on white, for the places it has to carry meaning on its own: a small
     * mark, or white text sitting on it. The brand coral is 3.05:1 and cannot.
     */
    coralInk: '#B84B2A',
    /** A wash of the deep teal, for card and tile surfaces that should read as part of the band. */
    tealWash: 'rgba(18, 65, 79, 0.045)',
    tealEdge: 'rgba(18, 65, 79, 0.14)',
    coralWash: 'rgba(238, 108, 78, 0.10)'
};

/**
 * A status ramp, not a categorical one: three named states of the same thing, in worsening order.
 * Checked as a set rather than picked by eye, worst adjacent pair 18.3 to normal vision and 14.7
 * under protanopia. Every state still ships with its label and a different fill length, because a
 * status colour on its own is never allowed to be the only thing carrying the meaning.
 */
export const DOMAIN_STATE_COLOR: { [key in DomainState]: string } = {
    usual: STATUS.steady,
    drifting: STATUS.declining,
    sharp: STATUS.incident
};

/**
 * The same states as a pill background, plus the grey for a client with no reading, darkened where
 * they had to be.
 *
 * White on the brand coral is 3.05:1, which fails for text at this size, so the pill takes a deeper
 * step of the same hue at 5.15:1. The wedges keep the brand coral: a large block of colour beside a
 * visible label is a different contrast question from small white text sitting on top of it.
 */
export const PILL_COLOR: { [key in OverallState]: string } = {
    usual: STATUS.steady,
    drifting: BRAND.coralInk,
    sharp: STATUS.incident,
    // The unknown grey darkened until white text on it reads at 5.18:1, which the map's grey does not.
    unscored: '#636F74'
};

/** The unfilled part of a wedge: what this person usually holds, that they are not holding now. */
export const DOMAIN_TRACK = '#D8D4CE';

/** Ink and furniture, kept recessive so the marks carry the chart. */
export const CHART_INK = {
    axis: 'var(--ion-color-step-250)',
    grid: 'var(--ion-color-step-100)',
    label: 'var(--ion-color-step-600)',
    strongLabel: 'var(--ion-color-step-850)',
    surface: 'var(--ion-background-color)'
};

/** Vertices of a pointy-top hexagon, which is the orientation the care map's honeycomb uses. */
export const hexPoints = (cx: number, cy: number, radius: number): string => {
    const halfWidth = (Math.sqrt(3) / 2) * radius;
    return [
        [cx, cy - radius],
        [cx + halfWidth, cy - radius / 2],
        [cx + halfWidth, cy + radius / 2],
        [cx, cy + radius],
        [cx - halfWidth, cy + radius / 2],
        [cx - halfWidth, cy - radius / 2]
    ].map(([x, y]) => `${Math.round(x * 100) / 100},${Math.round(y * 100) / 100}`).join(' ');
};

/** Centre of the hex at a grid position, with odd rows nudged half a cell right so they interlock. */
export const hexCentre = (params: { column: number; row: number; radius: number }): { cx: number; cy: number } => {
    const { column, row, radius } = params;
    const width = Math.sqrt(3) * radius;
    return {
        cx: width * (column + (row % 2 === 1 ? 0.5 : 0)) + width / 2,
        cy: radius * 1.5 * row + radius
    };
};

/**
 * Corners of a FLAT-TOP hexagon, clockwise from the top-left corner of the flat top edge.
 *
 * Flat-top rather than the pointy-top the honeycomb uses, because here the six EDGES carry meaning
 * rather than the cells: a flat-top hexagon's edges face top, upper-right, lower-right, bottom,
 * lower-left and upper-left, which is exactly the six positions the domain labels need.
 */
export const flatTopHexCorners = (params: { cx: number; cy: number; radius: number }): { x: number; y: number }[] => {
    const { cx, cy, radius } = params;
    // Start at 240 degrees so corner 0 is the top-left, making edge 0 the flat top edge.
    return [0, 1, 2, 3, 4, 5].map(index => {
        const radians = ((240 + index * 60) * Math.PI) / 180;
        return {
            x: Math.round((cx + radius * Math.cos(radians)) * 100) / 100,
            y: Math.round((cy + radius * Math.sin(radians)) * 100) / 100
        };
    });
};

/** A point a fraction of the way from one corner to the next. */
export const alongEdge = (from: { x: number; y: number }, to: { x: number; y: number }, fraction: number) => ({
    x: Math.round((from.x + (to.x - from.x) * fraction) * 100) / 100,
    y: Math.round((from.y + (to.y - from.y) * fraction) * 100) / 100
});

/**
 * A narrowing read back as a phrase, so a dimmed page never leaves the reader guessing why.
 *
 * Assembled from the sentence-case labels rather than from the display ones, because it lands
 * mid-sentence, and in a fixed order so that stacking two selections reads the same way whichever
 * of them the reader picked first.
 */
export const describeFilter = (filter: IInsightsFilter): string => {
    const parts: string[] = [];
    if (filter.kind) {
        parts.push(KIND_SENTENCE_LABEL[filter.kind]);
    }
    if (filter.standing) {
        parts.push(STANDING_SENTENCE_LABEL[filter.standing]);
    }
    return parts.join(', ');
};
